/** @prettier */
// @flow
import React, { useState, useMemo } from 'react';

import type { StoreVirtualCurrency } from '@site-builder/common/src/types/store';
import type { FiveSymbolLocale } from '@site-builder/common/src/utils/locale';

import CustomAmountItem from '../../../components/Blocks/Store/StoreItem/CustomAmountItem';
import { sendClick, XAEvents } from '../../../utils/init-xsolla-analytics';
import { CUSTOM_AMOUNT } from '../../../utils/store-helper';
import { analyticsManager } from '../../afterBlocks/analytics/analytics';
import { AnalyticsActions } from '../../afterBlocks/analytics/analyticsActions';
import { AnalyticsCategories } from '../../afterBlocks/analytics/analyticsCategories';
import { XsollaMetrikaEventNames } from '../../afterBlocks/analytics/analyticsEventsList';

export const DEFAULT_QUANTITY_CURRENCY = 100;
export const STEP = 10;
export const MAX_NUMBER = 999999;
export const MIN_NUMBER = 1;

type Props = {
  locale: FiveSymbolLocale,
  item: StoreVirtualCurrency,
  merchantId: string,
  blockId: string,
  onClickCustomAmount: ({
    buttonId: string,
    item: StoreVirtualCurrency,
    quantity: number,
  }) => void,
};
export const CustomAmountOnLanding = ({
  locale,
  item,
  merchantId,
  blockId,
  onClickCustomAmount,
}: Props) => {
  const [quantity, setQuantity] = useState(DEFAULT_QUANTITY_CURRENCY);
  const price = useMemo(
    () => (quantity * Number(item?.price?.amount)).toFixed(2),
    [quantity, item?.price?.amount]
  );
  const buttonId = `custom-amount-${blockId}-${item.sku}`;

  const changeQuantity = (value) => {
    let newValue = value;
    if (value > MAX_NUMBER) {
      newValue = MAX_NUMBER;
    } else if (value < MIN_NUMBER) {
      newValue = MIN_NUMBER;
    }
    setQuantity(newValue);
    return newValue;
  };

  const handleIncrement = () => {
    changeQuantity(quantity + STEP);
    analyticsManager.sendEvent({
      category: AnalyticsCategories.STORE,
      event: AnalyticsActions.ADD_VC,
      page: `landing_store_${AnalyticsActions.ADD_VC}`,
    });
  };

  const handleDecrement = () => {
    changeQuantity(quantity - STEP);
    analyticsManager.sendEvent({
      category: AnalyticsCategories.STORE,
      event: AnalyticsActions.REDUCE_VC,
      page: `landing_store_${AnalyticsActions.REDUCE_VC}`,
    });
  };

  const handleChange = (event) => {
    const value = changeQuantity(Number(event?.target?.value));
    sendClick(merchantId, XAEvents.customAmountInput, String(value));
  };

  const handleFocus = () =>
    sendClick(merchantId, XAEvents.customAmountInputFocus);

  const handleBuy = () => {
    onClickCustomAmount({ buttonId, item, quantity });
    sendClick(
      merchantId,
      XsollaMetrikaEventNames.OPEN_PS_FROM_STORE,
      CUSTOM_AMOUNT,
      {
        item,
        quantity,
        price,
        currency: item.price?.currency,
      }
    );
  };

  return (
    <CustomAmountItem
      locale={locale}
      isServerSideRender
      item={item}
      price={price}
      quantity={quantity}
      handleFocus={handleFocus}
      handleIncrement={handleIncrement}
      handleDecrement={handleDecrement}
      handleChange={handleChange}
      handleBuy={handleBuy}
      buttonId={buttonId}
      currency={item.price?.currency}
    />
  );
};
